import { useState, useEffect } from "react";
import { api } from "./api";

/**
 * /auth/config, fetched once per page load and shared.
 *
 * Several unrelated views need the same two server-side display settings — the
 * timezone (every table with a timestamp) and the session length (the transfer
 * banners). Prop-drilling them from a common ancestor would mean threading a
 * value through components that have nothing to do with it, and calling the
 * endpoint from each would mean five identical requests on every navigation.
 *
 * The result is cached at module level, so the first component to ask starts
 * the request and everyone else joins it. Subscribers exist so that when an
 * admin *changes* one of these settings, the views already on screen update —
 * see invalidatePortalConfig.
 */
const FALLBACK = { display_timezone: undefined, session_hours: null, project_statuses: null,
                   // Hidden rather than shown-and-broken while the config is still in flight
                   // or after a failed request — the Sync button needs a configured server.
                   ppms_enabled: false,
                   // The two Portal-settings switches fall back to ON — the server's
                   // default, and the side that offers no more than the server allows
                   // with them on: the approval queue is shown, and a create button
                   // the server may refuse with a message naming the setting.
                   document_four_eyes: true, manual_project_creation: true,
                   // The server's default: customer documents are priced quotations.
                   quotation_kind: "quotation",
                   // The sign-in page's help links (2026-11-04): none until the server answers.
                   links: [] };

let cached = null;
// The answer once it has arrived, so a view mounted LATER starts from it rather
// than from FALLBACK and a re-render a moment after: since 2026-11-03 most
// screens read `quotation_kind` (documentWord.js), and a card opened after the
// page loaded would otherwise say "quotation" for a frame before its word.
let resolved = null;
const listeners = new Set();

export function fetchPortalConfig() {
  if (!cached) {
    // Never rejects: a failed config request must degrade to browser-local
    // times and a hidden banner, not break every page that reads it — and that
    // includes a request that throws before it is even made.
    cached = new Promise(ok => ok(api.get("/auth/config")))
      .catch(() => FALLBACK)
      .then(c => { resolved = c || FALLBACK; return c; });
  }
  return cached;
}

/**
 * Re-read the settings and tell every mounted view.
 *
 * Called after saving a setting that lives in /auth/config. Without it the
 * admin changes the display timezone, the PATCH succeeds, the server reports
 * the new value — and every view on screen keeps formatting with the old one
 * until a full page reload, because the promise above is cached for the
 * lifetime of the document. Which is exactly what "I saved Vienna and still
 * see UTC" looks like.
 */
export function invalidatePortalConfig() {
  cached = null;
  resolved = null;
  fetchPortalConfig().then(c => listeners.forEach(fn => fn(c || FALLBACK)));
}

/** Test seam — the module cache would otherwise leak between test cases. */
export function __resetPortalConfig(value = null) {
  cached = value === null ? null : Promise.resolve(value);
  resolved = value;
}

export function usePortalConfig() {
  const [config, setConfig] = useState(() => resolved || FALLBACK);
  useEffect(() => {
    let alive = true;
    const apply = c => { if (alive && c) setConfig(c); };
    fetchPortalConfig().then(apply);
    // Re-render this view when the settings change under it.
    listeners.add(apply);
    return () => { alive = false; listeners.delete(apply); };
  }, []);
  return config;
}

/**
 * Just the display timezone, which is what most call sites want.
 * `undefined` until it arrives, which formatWhen reads as "the browser's own
 * zone" — the pre-setting behaviour, and the right thing to show meanwhile.
 */
export function useDisplayTimezone() {
  return usePortalConfig().display_timezone;
}
